import { fromPromise, setup } from "xstate";

import { getDocumentDirPath } from "~/data/filesystem";
import { filesystemApi } from "~/state/api/filesystem";
import { giteaApi } from "~/state/api/gitea";
import { sqliteApi } from "~/state/api/sqlite";
import { authenticationActorRef } from "~/state/machines/authentication";
import { setRepoPublished } from "~/state/machines/docsapi/setRepoPublished";
import { ALL_USERS, type Workspace } from "~/state/types";

const deleteDocumentFromDisk = fromPromise<void, { documentId: string }>(async ({ input }) => {
	const path = await getDocumentDirPath(input.documentId);

	await filesystemApi.document.delete(path);
});

const deleteDocumentFromRemote = fromPromise<
	void,
	{ documentId: string; workspaceNames: string[] }
>(async ({ input }) => {
	await Promise.all(
		input.workspaceNames.map((workspaceName) => {
			return giteaApi.document.delete(workspaceName, input.documentId);
		})
	);
});

const deleteDocumentFromCache = fromPromise<void, { documentId: string }>(async ({ input }) => {
	await Promise.all([
		sqliteApi.documents.delete(input.documentId),
		sqliteApi.documentWorkspaces.deleteByDocumentId(input.documentId),
	]);
});

const ensureUnpublished = fromPromise<void, { documentId: string; workspaceNames: string[] }>(
	async ({ input: { documentId, workspaceNames } }) => {
		await Promise.all(
			workspaceNames.map((workspaceName) => {
				return setRepoPublished({
					input: {
						workspaceName,
						documentId,
						type: "revoke",
						user: ALL_USERS,
						authenticationActorRef,
						skip: false,
					},
				});
			})
		);
	}
);

export const deleteDocumentMachine = setup({
	types: {
		context: {} as {
			documentId: string;
			workspaces: Workspace[];
		},
		input: {} as {
			documentId: string;
			workspaces: Workspace[];
		},
	},
	actors: {
		deleteDocumentFromDisk,
		deleteDocumentFromRemote,
		deleteDocumentFromCache,
		ensureUnpublished,
	},
}).createMachine({
	id: "delete-document",
	context: ({ input }) => ({
		documentId: input.documentId,
		workspaces: input.workspaces,
	}),
	initial: "unpublishing",
	states: {
		unpublishing: {
			invoke: {
				src: "ensureUnpublished",
				input: ({ context }) => ({
					documentId: context.documentId,
					workspaceNames: context.workspaces.map((w) => w.slug),
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Unable to unpublish the document", {
							cause: event.error,
						});
					},
				},
				onDone: {
					target: "deletingFromDisk",
				},
			},
		},
		deletingFromDisk: {
			invoke: {
				src: "deleteDocumentFromDisk",
				input: ({ context }) => ({
					documentId: context.documentId,
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Unable to delete document from disk", {
							cause: event.error,
						});
					},
				},
				onDone: {
					target: "deletingFromRemote",
				},
			},
		},
		deletingFromRemote: {
			invoke: {
				src: "deleteDocumentFromRemote",
				input: ({ context }) => ({
					documentId: context.documentId,
					workspaceNames: context.workspaces.map((w) => w.slug),
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Unable to delete document from remote", {
							cause: event.error,
						});
					},
				},
				onDone: {
					target: "deletingFromCache",
				},
			},
		},
		deletingFromCache: {
			invoke: {
				src: "deleteDocumentFromCache",
				input: ({ context }) => ({
					documentId: context.documentId,
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Unable to delete document from cache", {
							cause: event.error,
						});
					},
				},
				onDone: {
					target: "done",
				},
			},
		},
		done: {
			type: "final",
		},
	},
});
